/**
 * The chat composer's speech-to-text (`ChatInput.vue`'s mic). Two paths (MAR-1423). A seat whose plan
 * has `voice_transcription` (Ultra) dictates live through the LLM gateway (`useLiveDictation`, Gemini
 * Live); every other plan keeps the browser's own `SpeechRecognition`, below, exactly as before. The
 * live path falls back to the browser one when its connection cannot open.
 *
 * Call it from a component's setup: it registers the mount and unmount hooks it needs.
 */
import { computed, onMounted, onUnmounted, ref, type Ref } from 'vue'
import { useLiveDictation } from '~/composables/useLiveDictation'
import { composeDictation, dictationMode } from '~/utils/dictation/pcm'

export function useComposerDictation(model: Ref<string>, hideMic: () => boolean) {
  const listening = ref(false)
  const speechSupported = ref(false)
  let recognition: any = null

  const { profile: dictationProfile } = useUserProfile()
  const liveDictation = useLiveDictation()
  const liveSupported = ref(false)
  const liveDictationOn = computed(() => dictationProfile.value?.feature_flags?.voice_transcription === true)
  let liveActive = false
  const showMic = computed(() => !hideMic() && (speechSupported.value || (liveDictationOn.value && liveSupported.value)))
  /**
   * The Ultra mic (`UltraDictationMic`): a plan with live dictation gets the copper-ring button, which
   * becomes the "Dictando 0:42" pill while a live dictation runs. A live attempt that fell back to the
   * browser's recognition shows today's plain mic instead, and so does every other plan.
   */
  const liveMicOn = ref(false)
  const showUltraMic = computed(() => !hideMic() && liveDictationOn.value && liveSupported.value && !(listening.value && !liveMicOn.value))
  const showPlainMic = computed(() => showMic.value && !showUltraMic.value)
  const dictationElapsedMs = ref(0)
  let dictationTimer: ReturnType<typeof setInterval> | null = null

  function startDictationClock() {
    stopDictationClock()
    const startedAt = Date.now()
    dictationElapsedMs.value = 0
    dictationTimer = setInterval(() => { dictationElapsedMs.value = Date.now() - startedAt }, 250)
  }

  function stopDictationClock() {
    if (dictationTimer) clearInterval(dictationTimer)
    dictationTimer = null
  }

  onMounted(() => {
    liveSupported.value = liveDictation.supported()
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) return
    speechSupported.value = true
    recognition = new SR()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'es-SV'
    recognition.onresult = (e: any) => {
      let transcript = ''
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript
      }
      model.value = transcript
    }
    recognition.onend = () => { listening.value = false }
    recognition.onerror = () => { listening.value = false }
  })

  function startBrowserDictation() {
    if (!recognition) {
      listening.value = false
      return
    }
    recognition.start()
    listening.value = true
  }

  async function toggleMic() {
    if (listening.value) {
      if (liveActive) liveDictation.stop()
      else recognition?.stop()
      return
    }
    const mode = dictationMode({ voiceTranscription: liveDictationOn.value, browserSpeech: speechSupported.value, microphone: liveSupported.value })
    if (mode !== 'live') {
      startBrowserDictation()
      return
    }
    listening.value = true
    liveActive = true
    liveMicOn.value = true
    startDictationClock()
    const base = model.value
    const started = await liveDictation.start({
      onText: (finals, interim) => { model.value = composeDictation(base, finals, interim) },
      onEnd: () => {
        liveActive = false
        liveMicOn.value = false
        stopDictationClock()
        listening.value = false
      },
    })
    if (!started) {
      liveActive = false
      liveMicOn.value = false
      stopDictationClock()
      startBrowserDictation()
    }
  }

  onUnmounted(() => {
    stopDictationClock()
    if (!listening.value) return
    if (liveActive) liveDictation.stop()
    else recognition?.stop()
  })

  return { listening, liveMicOn, showUltraMic, showPlainMic, dictationElapsedMs, toggleMic }
}
